import React, { useState, useEffect, useMemo, useRef } from "react";
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";

import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Paper,
	Checkbox,
	TablePagination,
	TextField,
	Box,
	FormControlLabel,
	Switch,
	Menu,
	MenuItem,
	IconButton,
	Typography,
	InputAdornment,
} from "@mui/material";

import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PushPinIcon from "@mui/icons-material/PushPin";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { ArrowDownwardRounded, ArrowUpwardRounded, CancelRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import TanstackFooterPagination from "./TanstackFooterPagination";
import { use } from "react";
import { TableLoadingOverlay } from "./data";

const defColumnMinWidth = 90;
const defCellpadding = 1;
const headerFontColor = grey[900];

const resolveAdornment = (adornment, params) => {
	if (!adornment) return null;
	return typeof adornment === "function" ? adornment(params) : adornment;
};

const removeDuplicatesByRowId = (rows, getRowId) => {
	const seen = new Set();
	return rows.filter((row) => {
		let id = getRowId(row);
		if (seen.has(id)) {
			return false;
		}
		seen.add(id);
		return true;
	});
};

const DefaultRenderCell = ({ value, column, row }) => {
	const start = resolveAdornment(column?.startAdornment, { value });
	const end = resolveAdornment(column?.endAdornment, { value });
	console.log(column, "fcvbnmnbvcvbnm");
	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				padding: defCellpadding,
				boxSizing: "border-box",
				whiteSpace: "nowrap",
				overflow: "hidden",
			}}>
			{start && <Box sx={{ opacity: 0.7 }}>{start}</Box>}

			<Typography
				sx={{
					flexGrow: 1,
					overflow: "hidden",
					textOverflow: "ellipsis",
					textAlign: column.align === "center" ? "center" : column.align === "right" ? "flex-end" : "flex-start",
				}}>
				{value ?? ""}
			</Typography>
			{end && <Box sx={{ opacity: 0.7 }}>{end}</Box>}
		</Box>
	);
};

const DefaultRenderHeader = ({ column, headerText, children }) => {
	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				padding: defCellpadding,
				boxSizing: "border-box",
			}}>
			<Typography
				variant="subtitle1"
				fontWeight={600}
				flex={1}
				color={headerFontColor}
				noWrap
				textAlign={
					column.meta?.headerAlign === "center" ? "center" : column.meta?.headerAlign === "right" ? "flex-end" : "flex-start"
				}>
				{headerText}
			</Typography>

			{children}
		</Box>
	);
};

const DefaultRenderEditCell = React.forwardRef(({ value, type = "text", column, onBlur, onKeyDown }, ref) => {
	const startAdornment = resolveAdornment(column?.startAdornment, { value });
	const endAdornment = resolveAdornment(column?.endAdornment, { value });
	const pr = endAdornment ? 4 : 1;
	const pl = startAdornment ? 4 : 1;

	return (
		<TextField
			inputRef={ref}
			defaultValue={value}
			type={type}
			variant="standard"
			autoFocus
			fullWidth
			onBlur={onBlur}
			onKeyDown={onKeyDown}
			InputProps={{
				disableUnderline: true,

				startAdornment: startAdornment ? (
					<InputAdornment
						position="start"
						sx={{
							position: "absolute",
							left: 8,
							height: "100%",
							maxHeight: "none",
						}}>
						{startAdornment}
					</InputAdornment>
				) : null,

				endAdornment: endAdornment ? (
					<InputAdornment
						position="end"
						sx={{
							position: "absolute",
							right: 8,
							height: "100%",
							maxHeight: "none",
						}}>
						{endAdornment}
					</InputAdornment>
				) : null,
			}}
			sx={{
				width: "100%",
				height: "100%",

				// InputBase wrapper
				"& .MuiInputBase-root": {
					height: "100%",
					padding: 0,
					alignItems: "center",
					pr,
					pl,
				},

				// actual input element
				"& input": {
					height: "100%",
					padding: "0 6px",
					boxSizing: "border-box",
					textAlign: column?.dataType === "DECIMAL" ? "right" : "left",
				},
			}}
		/>
	);
});

const TanstackDatagrid = ({
	columns = [],
	headerHeight = 50,
	headerRowStyle = { background: grey[200] },
	rowHeight = 45,
	pageData: pageDataProp,
	setPageData: setPageDataProp,
	rowCount: rowCountProp,
	setRowCount: setRowCountProp,
	filterObj: filterObjProp,
	setFilterObj: setFilterObjProp,
	onPageChange = () => {},
	onSortChange = () => {},
	onColumnFilterChange = () => {},
	validateCell = null,
	isLoading = false,
	emptyMessage = "No records found",
	processRowUpdate = (newRow, oldRow, column) => {},
	handleRowClick = ({ row }, e) => {
		// Handle row click logic here
	},
	isCellEditable = ({ row, column }) => {
		return column.editable || false;
	},
	checkboxSelection = false,
	processCheckboxSelectionChange = (selectedIds) => {},
	getRowId = (row) => row.id,
	disablePagination = false,
	rowsPerPage = 50,
	serverSidePagination = true,
	disableCache = false,
	getServerSideData = (limit, offset, filterObj) => ({ rows: [], total_rows_count: 0 }),
	children: headerJSX,
}) => {
	const isPaginationEnabled = !disablePagination;

	const [columnVisibility, setColumnVisibility] = useState({});
	const [columnPinning, setColumnPinning] = useState({ left: [], right: [] });
	const [editingCell, setEditingCell] = useState(null);
	const [validationErrors, setValidationErrors] = useState({});
	// row selection states
	const [rowSelection, setRowSelection] = useState({});
	const [selectedRowIds, setSelectedRowIds] = useState([]);

	//  Local States
	const [pageDataState, setPageDataState] = useState([]);
	const [rowCountState, setRowCountState] = useState(0);
	const [columnFiltersState, setColumnFiltersState] = useState({});

	// checking for prop states
	const localData = typeof setPageDataProp === "function" ? pageDataProp : pageDataState;
	const setLocalData = typeof setPageDataProp === "function" ? setPageDataProp : setPageDataState;

	const serverRowCount = typeof setRowCountProp === "function" ? rowCountProp : rowCountState;
	const setServerRowCount = typeof setRowCountProp === "function" ? setRowCountProp : setRowCountState;

	const columnFilters = typeof setFilterObjProp === "function" ? filterObjProp : columnFiltersState;
	const setColumnFilters = typeof setFilterObjProp === "function" ? setFilterObjProp : setColumnFiltersState;

	const [currentPageState, setCurrentPageState] = useState(0);

	const inputRef = useRef(null);

	const [sorting, setSorting] = useState([]);

	// serverside pagination states
	const [offset, setOffset] = useState(0);
	const containerRef = useRef(null);
	const abortRef = useRef(null);
	const debounceRef = useRef(null);
	const cacheRef = useRef(new Map());

	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);

	//

	const safeColumns = useMemo(
		() =>
			Array.isArray(columns)
				? columns.map((col) => ({
						key: col?.key,
						name: col?.name || col.key.split("_").join(" ").toUpperCase(),
						editable: Boolean(col?.editable),
						renderCell: col?.renderCell || DefaultRenderCell,
						renderHeader: col?.renderHeader || DefaultRenderHeader,
						headerAlign: col?.headerAlign || "left",
						renderEditCell: col?.editable ? col?.renderEditCell || DefaultRenderEditCell : null,
						constraints: col?.constraints || {},
						flex: col?.flex ?? 0,
						minWidth: col?.minWidth ?? defColumnMinWidth,
						width: col?.width || undefined,
						dataType: col?.data_type,
						startAdornment: col?.startAdornment ?? null,
						endAdornment: col?.endAdornment ?? null,
						align: col?.align || "left",
					}))
				: [],
		[columns],
	);

	const tanstackColumns = useMemo(
		() =>
			safeColumns.map((col) => ({
				accessorKey: col.key,
				header: col.name,
				size: col.width,
				minSize: col.minWidth,
				cell: (info) =>
					col.renderCell
						? col.renderCell({ value: info.getValue(), row: info.row, column: info.column.columnDef.meta })
						: info.getValue(),
				meta: {
					key: col.key,
					editable: col.editable,
					renderEditCell: col.renderEditCell,
					renderHeader: col.renderHeader,
					headerAlign: col.headerAlign,
					constraints: col.constraints,
					flex: col.flex,
					minWidth: col.minWidth,
					width: col.width,
					dataType: col.dataType,
					startAdornment: col.startAdornment,
					endAdornment: col.endAdornment,
					align: col.align,
				},
			})),
		[safeColumns],
	);

	const table = useReactTable({
		data: localData,
		columns: tanstackColumns,
		pageCount: isPaginationEnabled ? Math.ceil(serverRowCount / rowsPerPage) : undefined,

		manualPagination: isPaginationEnabled,
		manualSorting: true,
		manualFiltering: true,
		enableRowSelection: checkboxSelection,

		state: {
			pagination: isPaginationEnabled ? { pageIndex: currentPageState, pageSize: rowsPerPage } : undefined,
			columnVisibility,
			columnPinning,
			sorting,
			rowSelection,
		},
		getRowId: (row) => getRowId(row),
		onSortingChange: (updater) => {
			const newSort = typeof updater === "function" ? updater(sorting) : updater;
			setSorting(newSort);
			onSortChange(newSort);
		},

		onColumnVisibilityChange: setColumnVisibility,
		onColumnPinningChange: setColumnPinning,

		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: isPaginationEnabled ? getPaginationRowModel() : undefined,
		getSortedRowModel: getSortedRowModel(),

		onSortingChange: (updater) => {
			const newSort = typeof updater === "function" ? updater(sorting) : updater;

			setSorting(newSort);
			onSortChange(newSort);

			if (!serverSidePagination) return;

			cacheRef.current.clear();
			setOffset(0);

			if (isPaginationEnabled) {
				setCurrentPageState(0);
			} else {
				setHasMore(true);
			}
		},
	});

	const handlePageChangeInternal = (nextPage) => {
		// 1. always update internal state
		// console.log( "sdfghjkjhgfds");
		setCurrentPageState(nextPage);
		setOffset(nextPage * rowsPerPage);

		// 2. call parent callback if provided
		if (typeof onPageChange === "function") {
			onPageChange(nextPage);
		}
	};
	const resetPaginationToFirstPage = () => {
		// reset page index
		setCurrentPageState(0);
		// reset server offset
		setOffset(0);

		// clear cached pages
		cacheRef.current.clear();
	};

	// debounce column filter changes and fetch data
	// useEffect(() => {
	// 	const t = setTimeout(() => {
	// 		onColumnFilterChange(columnFilters);

	// 		if (isPaginationEnabled && serverSidePagination) {
	// 			resetPaginationToFirstPage();
	// 			fetchServerData({ reset: true });
	// 		}
	// 	}, 400);

	// 	return () => clearTimeout(t);
	// }, [columnFilters]);

	useEffect(() => {
		if (editingCell && inputRef.current) {
			inputRef.current.focus();
		}
	}, [editingCell]);

	const handleCellBlur = (row, columnId, value, constraints) => {
		const rowId = getRowId(row);

		const error = validateCell ? validateCell(row, columnId, value, constraints) : null;

		setValidationErrors((prev) => ({
			...prev,
			[`${rowId}_${columnId}`]: error || null,
		}));

		if (error) return;

		const updatedRow = { ...row, [columnId]: value };
		const finalRow = processRowUpdate(updatedRow, row, columnId) || updatedRow;

		setLocalData((prev) => prev.map((r) => (getRowId(r) === rowId ? finalRow : r)));

		setEditingCell(null);
	};

	const handleKeyDown = (e, rowIndex, columnIndex, row, columnId, constraints) => {
		const id = getRowId(row);
		if (e.key === "Enter") {
			handleCellBlur(row, columnId, e.target.value, constraints);

			const nextRow = table.getRowModel().rows[rowIndex + 1];
			if (nextRow) {
				setEditingCell({
					rowId: getRowId(nextRow.original),
					columnId,
				});
			}
		}

		if (e.key === "Tab") {
			e.preventDefault();

			const cells = table.getRowModel().rows[rowIndex]?.getVisibleCells();

			const nextCell = e.shiftKey ? cells[columnIndex - 1] : cells[columnIndex + 1];

			if (nextCell) {
				setEditingCell({
					rowId: id,
					columnId: nextCell.column.id,
				});
			}
		}
	};

	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const togglePin = (colId, side) => {
		setColumnPinning((prev) => ({
			left:
				side === "left"
					? prev.left.includes(colId)
						? prev.left.filter((c) => c !== colId)
						: [...prev.left, colId]
					: prev.left.filter((c) => c !== colId),

			right:
				side === "right"
					? prev.right.includes(colId)
						? prev.right.filter((c) => c !== colId)
						: [...prev.right, colId]
					: prev.right.filter((c) => c !== colId),
		}));
	};

	// server side pagination function
	const fetchServerData = ({ reset = false } = {}) => {
		if (!serverSidePagination) return;

		if (abortRef.current) abortRef.current.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		const currentOffset = reset ? 0 : offset;

		const cacheKey = JSON.stringify({
			limit: rowsPerPage,
			offset: currentOffset,
			filters: columnFilters,
			sort: sorting,
		});

		if (!disableCache && cacheRef.current.has(cacheKey)) {
			const cached = cacheRef.current.get(cacheKey);
			setLocalData((prev) => (isPaginationEnabled ? cached.rows : reset ? cached.rows : [...prev, ...cached.rows]));
			setHasMore(cached.hasMore);
			return;
		}

		clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(async () => {
			setLoading(true);
			try {
				const res = await getServerSideData(rowsPerPage, currentOffset, columnFilters, controller.signal);

				const rows = removeDuplicatesByRowId(res?.rows || [], getRowId);
				const total = res?.total_rows_count ?? 0;
				setServerRowCount(total);
				const nextOffset = currentOffset + rows.length;
				const more = nextOffset < total;

				cacheRef.current.set(cacheKey, { rows, hasMore: more });

				setLocalData((prev) => (isPaginationEnabled ? rows : reset ? rows : [...prev, ...rows]));
				setOffset(nextOffset);
				setHasMore(more);
			} catch (e) {
				if (e.name !== "AbortError") console.error(e);
			} finally {
				setLoading(false);
			}
		}, 300);
	};

	useEffect(() => {
		if (isPaginationEnabled || !serverSidePagination) return;

		const el = containerRef.current;
		if (!el) return;

		const onScroll = () => {
			if (loading || !hasMore) return;

			const threshold = 120;
			if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
				fetchServerData();
			}
		};

		el.addEventListener("scroll", onScroll);
		return () => el.removeEventListener("scroll", onScroll);
	}, [loading, hasMore, disablePagination, serverSidePagination]);

	useEffect(() => {
		if (!serverSidePagination) return;

		clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(() => {
			// notify parent (if any)
			onColumnFilterChange(columnFilters);

			// reset cache + paging
			cacheRef.current.clear();
			setOffset(0);

			if (isPaginationEnabled) {
				// pagination mode
				setCurrentPageState(0);
				fetchServerData({ reset: true });
			} else {
				// infinite scroll mode
				setHasMore(true);
				fetchServerData({ reset: true });
			}
		}, 400);

		return () => clearTimeout(debounceRef.current);
	}, [columnFilters]);

	useEffect(() => {
		if (!isPaginationEnabled || !serverSidePagination) return;

		const nextOffset = +currentPageState + +rowsPerPage;
		setOffset(nextOffset);
		cacheRef.current.clear();
		fetchServerData({ reset: false });
	}, [currentPageState, rowsPerPage]);
	return (
		<Paper
			sx={{
				pl: 2,
				pr: 2,
				pb: 1,
				pt: 1,
				width: "100%",
				height: "100%",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				boxSizing: "border-box",
			}}>
			<Box sx={{ display: "flex", justifyContent: "space-between" }}>
				{
					<Box flexGrow={1} boxSizing="border-box">
						{headerJSX}
					</Box>
				}
				<IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
					<ViewColumnIcon />
				</IconButton>
			</Box>

			<Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
				{table.getAllLeafColumns().map((col) => (
					<MenuItem key={col.id}>
						<FormControlLabel
							control={<Switch checked={col.getIsVisible()} onChange={col.getToggleVisibilityHandler()} />}
							label={col.columnDef.header}
						/>
						<IconButton size="small" onClick={() => togglePin(col.id, "left")}>
							<PushPinIcon fontSize="inherit" />
						</IconButton>
					</MenuItem>
				))}
			</Menu>

			<Box ref={containerRef} sx={{ position: "relative", flex: 1, overflow: "auto", minHeight: 0, boxSizing: "border-box" }}>
				<Table sx={{ tableLayout: "fixed" }}>
					<TableHead>
						{table.getHeaderGroups().map((hg) => (
							<TableRow
								key={hg.id}
								sx={{ position: "sticky", top: 0, zIndex: 3, ...headerRowStyle, height: headerHeight }}>
								{checkboxSelection && (
									<TableCell padding="checkbox" sx={{ width: 48, height: headerHeight }}>
										<Checkbox
											checked={table.getIsAllRowsSelected()}
											indeterminate={table.getIsSomeRowsSelected()}
											onChange={table.getToggleAllRowsSelectedHandler()}
										/>
									</TableCell>
								)}

								{hg.headers.map((header) => {
									const colId = header.column.id;
									const isFiltering = columnFilters[colId] !== undefined;

									const actionBtns = (
										<>
											<IconButton
												className="header-actions"
												size="small"
												sx={{
													opacity: isFiltering ? 1 : 0.6,
													color: headerFontColor,
												}}
												onClick={() =>
													setColumnFilters((p) => ({
														...p,
														[colId]: p[colId] === undefined ? "" : undefined,
													}))
												}>
												{isFiltering ? (
													<CancelRounded fontSize="inherit" color="inherit" />
												) : (
													<FilterAltIcon fontSize="inherit" color="inherit" />
												)}
											</IconButton>
											<IconButton
												className="header-actions"
												size="small"
												onClick={header.column.getToggleSortingHandler()}
												sx={{
													opacity: header.column.getIsSorted() ? 1 : 0.6,
													color: headerFontColor,
												}}>
												{header.column.getIsSorted() === "asc" ? (
													<ArrowUpwardRounded fontSize="inherit" color="inherit" />
												) : (
													<ArrowDownwardRounded fontSize="inherit" color="inherit" />
												)}
											</IconButton>
										</>
									);

									return (
										<TableCell
											key={header.id}
											sx={{
												minWidth: header.column.columnDef.meta?.minWidth || defColumnMinWidth,
												width: header.column.columnDef.meta?.width || "auto",
												flex: header.column.columnDef.meta?.flex || "initial",
												height: headerHeight,
												padding: 0,
											}}>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													width: "100%",
													height: "100%",
													//  default: truly hidden
													"& .header-actions": {
														display: !isFiltering ? "none" : "inline-flex",
														opacity: isFiltering ? 1 : 0,
														transition: "opacity 0.15s ease-in-out",
													},

													//  on hover: show + fade in
													"&:hover .header-actions": {
														display: "inline-flex", // keeps icon button sizing correct
														opacity: 1,
													},
												}}>
												{isFiltering ? (
													<>
														<Box
															sx={{
																flex: 1,
																minWidth: 0,
																height: "100%",
																display: "flex",
																alignItems: "center",
																p: defCellpadding,
																boxSizing: "border-box",
																boxShadow: `inset 5px 5px 10px ${grey[300]}`,
																backgroundColor: grey[100],
															}}>
															<TextField
																variant="standard"
																autoFocus
																placeholder={`Filter ${header.column.columnDef.header}`}
																value={columnFilters[colId] || ""}
																onChange={(e) =>
																	setColumnFilters((p) => ({
																		...p,
																		[colId]: e.target.value || undefined,
																	}))
																}
																fullWidth
															/>
															{actionBtns}
														</Box>
													</>
												) : (
													header.column.columnDef.meta.renderHeader({
														column: header.column.columnDef,
														headerText: header.column.columnDef.header,
														children: actionBtns,
													})
												)}
											</Box>
										</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableHead>

					{/* BODY — COMPLETELY UNCHANGED */}
					{loading && isPaginationEnabled ? (
						<></>
					) : (
						<TableBody>
							{table.getRowModel().rows.map((row, rIndex) => {
								const id = getRowId(row.original);
								return (
									<TableRow
										key={id}
										selected={row.getIsSelected()}
										hover
										sx={{ height: rowHeight }}
										onClick={(e) => handleRowClick({ row: row.original }, e)}>
										{" "}
										{checkboxSelection && (
											<TableCell padding="checkbox">
												<Checkbox checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} />
											</TableCell>
										)}
										{row.getVisibleCells().map((cell, cIndex) => {
											const meta = cell.column.columnDef.meta || {};
											const isEditing = editingCell?.rowId === id && editingCell?.columnId === cell.column.id;

											const error = validationErrors[`${id}_${cell.column.id}`];

											return (
												<TableCell
													key={cell.id}
													onDoubleClick={() => {
														if (
															isCellEditable({
																row: row.original,
																column: cell.column.columnDef.meta,
															})
														) {
															setEditingCell({
																rowId: id,
																columnId: cell.column.id,
															});
														}
													}}
													sx={{
														minWidth: cell.column.columnDef.meta?.minWidth || defColumnMinWidth,
														width: cell.column.columnDef.meta?.width || "auto",
														flex: cell.column.columnDef.meta?.flex || "initial",
														cursor: meta.editable ? "text" : "default",
														// "&:hover": {
														// 	backgroundColor: meta.editable ? "rgba(25,118,210,0.05)" : "inherit",
														// },
														p: 0,
														height: rowHeight,
														overflow: "hidden",
													}}>
													{isEditing && meta.renderEditCell ? (
														<Box
															sx={{
																width: "100%",
																height: "100%",
																display: "flex",
																alignItems: "stretch",
																// padding: defCellpadding,
																boxSizing: "border-box",
																border: error ? `2px solid red` : `2px solid ${grey[400]}`,
																borderRadius: 1,
															}}>
															<meta.renderEditCell
																ref={inputRef}
																value={cell.getValue()}
																type={meta.dataType === "DECIMAL" ? "number" : "text"}
																// column={cell.column.columnDef.meta}
																// onChange={(e) => {
																// 	editBufferRef.current.value = e.target.value;
																// }}
																onBlur={(e) =>
																	handleCellBlur(
																		row.original,
																		cell.column.id,
																		e.target.value,
																		meta.constraints,
																	)
																}
																onKeyDown={(e) =>
																	handleKeyDown(
																		e,
																		rIndex,
																		cIndex,
																		row.original,
																		cell.column.id,
																		meta.constraints,
																	)
																}
															/>
														</Box>
													) : (
														<Box sx={{ py: 0.5, boxSizing: "border-box" }}>
															{flexRender(cell.column.columnDef.cell, cell.getContext())}
														</Box>
													)}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
						</TableBody>
					)}
				</Table>
				{/* FULL TABLE LOADING (FIRST LOAD) */}
				{((loading && localData.length > 0) || (loading && isPaginationEnabled)) && (
					<TableLoadingOverlay
						columnsCount={table.getVisibleLeafColumns().length + (checkboxSelection ? 1 : 0)}
						rowHeight={rowHeight}
						rowCount={rowsPerPage}
					/>
				)}
				{!isLoading && localData.length === 0 && (
					<Typography align="center" color="text.secondary" sx={{ mt: 3 }}>
						{emptyMessage}
					</Typography>
				)}
				{loading && localData.length > 0 && (
					<Box sx={{ py: 1, textAlign: "center", backgroundColor: grey[50] }}>
						<Typography variant="body1" color="text.secondary">
							Loading…
						</Typography>
					</Box>
				)}
			</Box>

			{isPaginationEnabled && (
				<TanstackFooterPagination
					count={serverRowCount}
					page={currentPageState}
					rowsPerPage={rowsPerPage}
					onPageChange={handlePageChangeInternal}
					// onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
				/>
			)}
		</Paper>
	);
};

export default TanstackDatagrid;
